import { UploadOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Checkbox, Form, Input, Select, Upload } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AdGroupBean, AdSizeBean, AdvertiserBean, CreativeBean } from 'src/api/api'
import { formDataLoader, FormDataType } from 'src/util/FormDataLoader'
import creativeApi from './CreativeApi'

const FormItem = Form.Item
const Option = Select.Option

const formatEnum = CreativeBean.FormatEnum

interface Props { }

const formInfoItems: FormDataType[] = [
    FormDataType.AdSizes,
    FormDataType.Advertisers
]

export const getFileLabel = (format: CreativeBean.FormatEnum) => {
    switch (format) {
        case formatEnum.Video: return 'Video files'
        default: return 'Images'
    }
}

const CreativeBulk: React.FC<Props> = () => {

    const [form] = useForm()
    const [error, setError] = useState()
    const navigateTo = useNavigate()

    const [iconToUpload, setIconTuUpload] = useState()

    const [filesToUpload, setFilesToUpload] = useState<any[]>([])
    const [sizesToUpload, setSizesToUpload] = useState<number[]>([])

    const [info, setInfo] = useState({
        adSizes: [],
        advertisers: []
    })

    const [params] = useSearchParams()

    const advertiserId = params.get('advertiserId')
    const adGroupId = params.get('adGroupId')

    const handleSubmit = async () => {
        const values = await form.validateFields()
        try {
            if (adGroupId) values.adGroupIds = [adGroupId]

            await creativeApi.createBulk(values, iconToUpload, filesToUpload, sizesToUpload)
            goBack()
        } catch (e) {
            setError(e.error)
        }
    }

    const loadData = async () => {
        try {
            const resp = await formDataLoader(...formInfoItems)
            setInfo(resp as any)
        } catch (e) {
            setError(e.error)
        }
    }

    useEffect(() => {
        loadData()
    }, [])

    const goBack = () => navigateTo(adGroupId ? `/ad-group/${adGroupId}/edit` : '/creative')

    const format = Form.useWatch('format', form)

    let tempCounter: number = 0
    const tempFiles: any[] = []
    const tempSizes: any[] = []
    const tempErrors: string[] = []

    return <Form
        style={{
            maxWidth: '1000px', margin: '0 auto'
        }}
        onFinish={handleSubmit}
        form={form}
        labelCol={{ span: 5 }}
        wrapperCol={{ span: 19 }}
    >
        {error && <Alert message={error} type="error" />}
        <Card title="Creative Bulk" size="small" className="form-card">
            <FormItem
                required
                name="name"
                label="Prefix name"
            >
                <Input />
            </FormItem>
            <FormItem
                required
                name="advertiserId"
                label="Advertiser"
                initialValue={advertiserId ? +advertiserId : undefined}
            >
                <Select showSearch optionFilterProp="title" placeholder="Select Advertiser">
                    {info.advertisers.map((item: AdvertiserBean) => <Option title={item.name} key={item.id} value={item.id}>{item.name}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                required
                name="format"
                label="Format"
            >
                <Select>
                    {Object.values([formatEnum.Banner, formatEnum.Video, formatEnum.Native]).map(type => <Option key={type} value={type}>{type}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                name="landingUrl"
                label="Landing URL"
            >
                <Input />
            </FormItem>
            <FormItem
                name="active"
                label="Active"
                valuePropName="checked"
                initialValue="true"
                required
            >
                <Checkbox />
            </FormItem>
            {format == formatEnum.Native && <FormItem
                name={['nativeData', 'title']}
                label="Title"
            >
                <Input />
            </FormItem>}
            {format == formatEnum.Native && <FormItem
                name={['nativeData', 'description']}
                label="Description"
            >
                <Input />
            </FormItem>}
            {format == formatEnum.Native && <FormItem
                name={['nativeData', 'cta']}
                label="Call to Action"
            >
                <Input />
            </FormItem>}
            {format == formatEnum.Native && <FormItem
                name={['nativeData', 'sponsored']}
                label="Sponsored by"
            >
                <Input />
            </FormItem>}
            {format == formatEnum.Native && <FormItem
                label="Icon"
            >
                <Upload
                    maxCount={1}
                    beforeUpload={(incomingFile: any) => {
                        setIconTuUpload(incomingFile)
                        return false
                    }}
                >
                    <Button icon={<UploadOutlined />}>Click to Upload</Button>
                </Upload>
            </FormItem>}
            <FormItem
                label={getFileLabel(format)}
            >
                <Upload
                    multiple
                    fileList={filesToUpload}
                    beforeUpload={async (incomingFile: any, incomingFiles: File[]) => {

                        const blobUrl = URL.createObjectURL(incomingFile)
                        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
                            const i = new Image()
                            i.onload = () => resolve(i)
                            i.onerror = reject
                            i.src = blobUrl
                        })

                        const { width: w, height: h } = img

                        const exist = (info.adSizes as AdSizeBean[]).find((size: AdSizeBean) => size.width == w && size.height == h)
                        if (exist) {
                            tempFiles.push(incomingFile)
                            tempSizes.push(exist.id as number)
                        } else {
                            tempErrors.push('File ' + incomingFile.name + ' does not have associated AdSize')
                        }

                        tempCounter++
                        if (incomingFiles.length == tempCounter) {

                            if (tempFiles.length > 0) {
                                const files = [...filesToUpload]
                                files.push(...tempFiles)
                                setFilesToUpload(files)

                                const sizes = [...sizesToUpload]
                                sizes.push(...tempSizes)
                                setSizesToUpload(sizes)
                            }

                            setError(tempErrors.length > 0 ? (tempErrors as any).join('\n') : undefined)
                        }

                        return false
                    }}
                    onRemove={removingFile => {
                        const index = filesToUpload.findIndex(file => file.uid == removingFile.uid)

                        const files = [...filesToUpload]
                        files.splice(index, 1)
                        setFilesToUpload(files)

                        const sizes = [...sizesToUpload]
                        sizes.splice(index, 1)
                        setSizesToUpload(sizes)
                    }}
                >
                    <Button icon={<UploadOutlined />}>Click to Upload</Button>
                </Upload>
            </FormItem>
        </Card>
        <div className="create-cancel-group">
            <Button type="primary" htmlType="submit">Create</Button>
            <Button htmlType="button" danger onClick={goBack}>Cancel</Button>
        </div>
    </Form>
}

export default CreativeBulk


// WEBPACK FOOTER //
// ./src/component/app/creative/CreativeBulk.tsx